import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PayeePopupComponent } from '../../shared/payee-popup/payee-popup.component';
import { PayeeButtonComponent } from '../../shared/payee-buttons/payee-button/payee-button.component';

export type SaveOrResendProFormaDialogResult = 'save' | 'save_and_send';

export type SaveOrResendProFormaDialogData = {
  title: string;
  description: string;
  autoSendOnSave?: boolean;
};

export type FeeChangeFollowUpKind = 'proforma' | 'invoice_asana';

type ClaimAmountSnapshot = {
  id?: string | null;
  remaining_amount?: number | null;
};

/** Matches backend fee calculation, which uses remaining principal amounts. */
export function isPrincipalClaimAmountDecrease(
  previousClaims: ClaimAmountSnapshot[],
  currentClaims: ClaimAmountSnapshot[],
): boolean {
  let previousTotal = 0;
  let currentTotal = 0;

  for (const claim of currentClaims) {
    const previous = previousClaims.find(
      (c) => c.id != null && c.id === claim.id,
    );
    previousTotal += Number(previous?.remaining_amount ?? 0);
    currentTotal += Number(claim.remaining_amount ?? 0);
  }

  return currentTotal < previousTotal;
}

export function getFeeChangeFollowUpKind(data: {
  is_waiting_for_payment: boolean;
  started_at: string | null;
  has_been_submitted_to_notary?: boolean;
}, options: {
  feeDecrease: boolean;
  feeIncrease: boolean;
}): FeeChangeFollowUpKind | null {
  if (data.has_been_submitted_to_notary) {
    return null;
  }

  if (options.feeDecrease && data.is_waiting_for_payment) {
    return 'proforma';
  }

  if (options.feeDecrease && data.started_at) {
    return 'invoice_asana';
  }

  if (
    options.feeIncrease
    && !options.feeDecrease
    && (data.is_waiting_for_payment || data.started_at)
  ) {
    return 'proforma';
  }

  return null;
}

@Component({
  selector: 'app-save-or-resend-pro-forma-dialog',
  templateUrl: './save-or-resend-pro-forma-dialog.component.html',
  styleUrls: ['./save-or-resend-pro-forma-dialog.component.scss'],
  imports: [PayeePopupComponent, PayeeButtonComponent],
})
export class SaveOrResendProFormaDialogComponent {
  private readonly dialogRef =
    inject<
      MatDialogRef<
        SaveOrResendProFormaDialogComponent,
        SaveOrResendProFormaDialogResult | undefined
      >
    >(MatDialogRef);
  private readonly data = inject<SaveOrResendProFormaDialogData | null>(
    MAT_DIALOG_DATA,
    { optional: true },
  );

  readonly title =
    this.data?.title ?? 'Díjbekérő újraküldése';
  readonly description =
    this.data?.description ??
    'A díjak módosulni fognak. Mentés után szeretnéd újraküldeni a díjbekérőt a hitelezőnek?';
  readonly autoSendOnSave = this.data?.autoSendOnSave ?? false;

  readonly saveOnly = (): void => {
    this.dialogRef.close(this.autoSendOnSave ? 'save_and_send' : 'save');
  };

  readonly saveAndSend = (): void => {
    this.dialogRef.close('save_and_send');
  };

  readonly cancel = (): void => {
    this.dialogRef.close(undefined);
  };
}
